import { handleError } from '@/actions/metaActions';
import {
    stateConfig,
    stateMapLayer,
    statePreferences,
    stateZoom,
} from '@/selectors/selectors';
import { Severity, VegkartError } from '@/state';
import { useMap } from '@/state/context/MapContext';
import { useDispatch, useSelector } from '@/state/store';
import { getNibTokenExpiryWarningMessage } from '@/utils/nibTokenExpiry';
import { applyStyle } from 'ol-mapbox-style';
import TileLayer from 'ol/layer/Tile';
import VectorTileLayer from 'ol/layer/VectorTile';
import { VectorTile } from 'ol/source';
import TileGrid from 'ol/tilegrid/TileGrid';
import { useEffect, useMemo } from 'react';
import {
    createGeodataSource,
    createGrayscaleLayerSource,
    createNiBLayerSource,
    createOpenStreetMapSource,
    createTopoLayerSource,
    kanvasResolutions,
    MapLayerType,
    resolveTileSource,
    TileSourceType,
} from '../map-utils/WebMapTileServices';

const LAYER_TYPE = 'type';
const BASE_LAYER_PRELOAD = Infinity;

export const TileServiceLayers = () => {
    const { map } = useMap();
    const dispatch = useDispatch();
    const config = useSelector(stateConfig);
    const visibleLayer = useSelector(stateMapLayer);
    const zoom = useSelector(stateZoom);
    const preferences = useSelector(statePreferences);

    const geodataLayer = useMemo(() => {
        const layer = new TileLayer({
            source: createGeodataSource(config),
            preload: BASE_LAYER_PRELOAD,
        });
        layer.set(LAYER_TYPE, TileSourceType.GEODATA);
        return layer;
    }, [config]);
    const nibLayer = useMemo(() => {
        const layer = new TileLayer({
            source: createNiBLayerSource(config),
            preload: BASE_LAYER_PRELOAD,
        });
        layer.set(LAYER_TYPE, TileSourceType.NIB);
        return layer;
    }, [config]);
    const topoLayer = useMemo(() => {
        const layer = new TileLayer({
            source: createTopoLayerSource(config),
            preload: BASE_LAYER_PRELOAD,
        });
        layer.set(LAYER_TYPE, TileSourceType.TOPO);
        return layer;
    }, [config]);

    const grayscaleLayer = useMemo(() => {
        const layer = new TileLayer({
            source: createGrayscaleLayerSource(config),
            preload: BASE_LAYER_PRELOAD,
        });
        layer.set(LAYER_TYPE, TileSourceType.TOPOGRAATONE);
        return layer;
    }, [config]);

    const kanvas = useMemo(() => {
        const layer = new VectorTileLayer({
            declutter: true,
            preload: BASE_LAYER_PRELOAD,
            properties: { [LAYER_TYPE]: TileSourceType.KANVAS },
            source: new VectorTile({
                tileGrid: new TileGrid({
                    tileSize: [512, 512],
                    origin: [-2500000, 9045984],
                    resolutions: kanvasResolutions,
                }),
                projection: map.getView().getProjection(),
            }),
        });
        applyStyle(layer, config.url_kanvas);
        return layer;
    }, [map, config.url_kanvas]);
    const kanvasDark = useMemo(() => {
        const layer = new VectorTileLayer({
            declutter: true,
            preload: BASE_LAYER_PRELOAD,
            properties: { [LAYER_TYPE]: TileSourceType.KANVAS_DARK },
            source: new VectorTile({
                tileGrid: new TileGrid({
                    tileSize: [512, 512],
                    origin: [-2500000, 9045984],
                    resolutions: kanvasResolutions,
                }),
                projection: map.getView().getProjection(),
            }),
        });
        applyStyle(layer, config.url_kanvas_mork);
        return layer;
    }, [map, config.url_kanvas_mork]);

    const basis = useMemo(() => {
        const layer = new VectorTileLayer({
            declutter: true,
            preload: BASE_LAYER_PRELOAD,
            properties: { [LAYER_TYPE]: TileSourceType.BASIS },
            source: new VectorTile({
                tileGrid: new TileGrid({
                    tileSize: [512, 512],
                    origin: [-2500000, 9045984],
                    resolutions: kanvasResolutions,
                }),
                projection: map.getView().getProjection(),
            }),
        });
        applyStyle(layer, config.url_basis);
        return layer;
    }, [map, config.url_basis]);

    const basisGray = useMemo(() => {
        const layer = new VectorTileLayer({
            declutter: true,
            preload: BASE_LAYER_PRELOAD,
            properties: { [LAYER_TYPE]: TileSourceType.BASIS_GRAY },
            source: new VectorTile({
                tileGrid: new TileGrid({
                    tileSize: [512, 512],
                    origin: [-2500000, 9045984],
                    resolutions: kanvasResolutions,
                }),
                projection: map.getView().getProjection(),
            }),
        });
        applyStyle(layer, config.url_basis_graatone);
        return layer;
    }, [map, config.url_basis_graatone]);

    const openStreetMapLayer = useMemo(() => {
        const layer = new TileLayer({
            source: createOpenStreetMapSource(config),
            preload: BASE_LAYER_PRELOAD,
        });
        layer.set(LAYER_TYPE, TileSourceType.OPENSTREETMAP);
        return layer;
    }, [config]);

    const layers = useMemo(
        () => [
            geodataLayer,
            nibLayer,
            topoLayer,
            grayscaleLayer,
            kanvas,
            kanvasDark,
            basis,
            basisGray,
            openStreetMapLayer,
        ],
        [
            geodataLayer,
            nibLayer,
            topoLayer,
            grayscaleLayer,
            kanvas,
            kanvasDark,
            basis,
            basisGray,
            openStreetMapLayer,
        ]
    );

    useEffect(() => {
        // Resolve which tile source should be visible based on selected layer and zoom
        const updateVisibleLayer = () => {
            const resolvedTileSource = resolveTileSource(
                visibleLayer,
                map.getView().getZoom() ?? zoom,
                preferences?.includeTopoWithStandardMap
            );
            layers.forEach(layer =>
                layer.setVisible(resolvedTileSource === layer.get(LAYER_TYPE))
            );
        };

        updateVisibleLayer();
        map.getView().on('change:resolution', updateVisibleLayer);
        return () => map.getView().un('change:resolution', updateVisibleLayer);
    }, [layers, map, visibleLayer, zoom, preferences]);

    useEffect(() => {
        layers.forEach(layer => map.addLayer(layer));
        return () => {
            layers.forEach(layer => map.removeLayer(layer));
        };
    }, [layers, map]);

    useEffect(() => {
        const warning = getNibTokenExpiryWarningMessage(config);
        if (warning) {
            dispatch(handleError(new VegkartError(warning, {}, Severity.WARN)));
        }
    }, [zoom, config, dispatch]);

    return null;
};
